/**
 * Games API module
 * Handles all game-related API operations
 */

import { db, supabase } from '../lib/supabase';
import { createAPIMethod } from './base';

/**
 * Games API namespace
 */
export const gameAPI = {
  /**
   * Create a new game
   * @param {Object} data - Game data
   * @returns {Promise<APIResponse>}
   */
  create: createAPIMethod(
    (data) => db.createGame(data),
    { operation: 'Create game' }
  ),

  /**
   * Create a new game as team manager
   * @param {Object} data - Game data
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  createAsTeamManager: createAPIMethod(
    (data, userId) => db.createGameAsTeamManager(data, userId),
    { operation: 'Create game as team manager' }
  ),

  /**
   * Update an existing game
   * @param {string} id - Game ID
   * @param {Object} data - Updated game data
   * @returns {Promise<APIResponse>}
   */
  update: createAPIMethod(
    (id, data) => db.updateGame(id, data),
    { operation: 'Update game' }
  ),

  /**
   * Update an existing game as team manager
   * @param {string} id - Game ID
   * @param {Object} data - Updated game data
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  updateAsTeamManager: createAPIMethod(
    (id, data, userId) => db.updateGameAsTeamManager(id, data, userId),
    { operation: 'Update game as team manager' }
  ),

  /**
   * Delete a game
   * @param {string} id - Game ID
   * @returns {Promise<APIResponse>}
   */
  delete: createAPIMethod(
    async (id) => {
      return await supabase
        .from('games')
        .delete()
        .eq('id', id);
    },
    { operation: 'Delete game' }
  ),

  /**
   * Get a single game by ID
   * @param {string} id - Game ID
   * @returns {Promise<APIResponse>}
   */
  getById: createAPIMethod(
    async (id) => {
      return await supabase
        .from('games')
        .select(`
          *,
          tournaments (
            id, name, timezone
          ),
          tournament_divisions (
            id, name, description, skill_level
          ),
          home_team:teams!home_team_id (
            id, name, company
          ),
          away_team:teams!away_team_id (
            id, name, company
          ),
          game_locations (
            id, name, city, state, address_line1
          )
        `)
        .eq('id', id)
        .single();
    },
    { operation: 'Get game' }
  ),

  /**
   * Get all games with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getAll: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('games')
        .select(`
          *,
          tournaments (
            id, name, timezone
          ),
          tournament_divisions (
            id, name, description, skill_level
          ),
          home_team:teams!home_team_id (
            id, name, company
          ),
          away_team:teams!away_team_id (
            id, name, company
          ),
          game_locations (
            id, name, city, state
          )
        `);

      // Apply filters
      if (filters.tournamentId) {
        query = query.eq('tournament_id', filters.tournamentId);
      }
      if (filters.teamId) {
        query = query.or(`home_team_id.eq.${filters.teamId},away_team_id.eq.${filters.teamId}`);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.dateFrom) {
        query = query.gte('starts_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('starts_at', filters.dateTo);
      }
      if (filters.locationId) {
        query = query.eq('location_id', filters.locationId);
      }

      // Add ordering
      query = query.order('starts_at', { ascending: true });

      return await query;
    },
    { operation: 'Get games' }
  ),

  /**
   * Get games by tournament
   * @param {string} tournamentId - Tournament ID
   * @returns {Promise<APIResponse>}
   */
  getByTournament: createAPIMethod(
    async (tournamentId) => {
      return await supabase
        .from('games')
        .select(`
          *,
          tournaments (
            id, name, timezone
          ),
          tournament_divisions (
            id, name, description, skill_level
          ),
          home_team:teams!home_team_id (
            id, name, company
          ),
          away_team:teams!away_team_id (
            id, name, company
          ),
          game_locations (
            id, name, city, state
          )
        `)
        .eq('tournament_id', tournamentId)
        .order('starts_at', { ascending: true });
    },
    { operation: 'Get games by tournament' }
  ),

  /**
   * Get games by team
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  getByTeam: createAPIMethod(
    async (teamId) => {
      return await supabase
        .from('games')
        .select(`
          *,
          tournaments (
            id, name, timezone
          ),
          tournament_divisions (
            id, name, description, skill_level
          ),
          home_team:teams!home_team_id (
            id, name, company
          ),
          away_team:teams!away_team_id (
            id, name, company
          ),
          game_locations (
            id, name, city, state
          )
        `)
        .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`)
        .order('starts_at', { ascending: true });
    },
    { operation: 'Get games by team' }
  ),

  /**
   * Get upcoming games
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getUpcoming: createAPIMethod(
    async (filters = {}) => {
      const now = new Date().toISOString();
      
      let query = supabase
        .from('games')
        .select(`
          *,
          tournaments (
            id, name, timezone
          ),
          tournament_divisions (
            id, name, description, skill_level
          ),
          home_team:teams!home_team_id (
            id, name, company
          ),
          away_team:teams!away_team_id (
            id, name, company
          ),
          game_locations (
            id, name, city, state
          )
        `)
        .gte('starts_at', now)
        .eq('status', 'scheduled');

      // Apply additional filters
      if (filters.tournamentId) {
        query = query.eq('tournament_id', filters.tournamentId);
      }
      if (filters.teamId) {
        query = query.or(`home_team_id.eq.${filters.teamId},away_team_id.eq.${filters.teamId}`);
      }
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      query = query.order('starts_at', { ascending: true });

      return await query;
    },
    { operation: 'Get upcoming games' }
  ),

  /**
   * Update game status
   * @param {string} id - Game ID
   * @param {string} status - New status
   * @returns {Promise<APIResponse>}
   */
  updateStatus: createAPIMethod(
    async (id, status) => {
      const validStatuses = ['scheduled', 'in_progress', 'completed', 'cancelled', 'postponed'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Invalid status: ${status}`);
      }

      return await supabase
        .from('games')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Update game status' }
  ),

  /**
   * Update game scores
   * @param {string} id - Game ID
   * @param {Object} scores - Score data
   * @returns {Promise<APIResponse>}
   */
  updateScores: createAPIMethod(
    async (id, { score_home, score_away }) => {
      return await supabase
        .from('games')
        .update({ 
          score_home: score_home !== null ? parseInt(score_home, 10) : null,
          score_away: score_away !== null ? parseInt(score_away, 10) : null,
          status: 'completed', // Automatically mark as completed when scores are updated
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Update game scores' }
  ),

  /**
   * Get game statistics
   * @param {string} id - Game ID
   * @returns {Promise<APIResponse>}
   */
  getStatistics: createAPIMethod(
    async (id) => {
      // This would be expanded based on what statistics are tracked
      const game = await supabase
        .from('games')
        .select(`
          *,
          home_team:teams!home_team_id (
            id, name
          ),
          away_team:teams!away_team_id (
            id, name
          )
        `)
        .eq('id', id)
        .single();

      if (game.error) throw game.error;

      return {
        game: game.data,
        // Add more statistics as needed
        duration: game.data.ends_at && game.data.starts_at ? 
          new Date(game.data.ends_at) - new Date(game.data.starts_at) : null
      };
    },
    { operation: 'Get game statistics' }
  )
};